import { axiosV2 } from "config/@axios";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import moment from "dayjs";

import * as mainUiActions from "store/actions/uiActions";
import * as entityActions from "./entityActions";
import { idDecode } from "utils/idFixer";
import { serverTimezone } from "config/environment";

export const OPEN_MODAL = "Calendar->uiActions::openModal";
export const CLOSE_MODAL = "Calendar->uiActions::closeModal";
export const SET_SENDING = "Calendar->uiActions::setSending";
export const REMIND_EVENT = "Calendar->uiActions::remindEvent";
export const CLEAN_REMINDERS = "Calendar->uiActions::cleanReminders";

export const SET_MODAL_ERROR_MESSAGE =
  "Calendar->uiActions::setModalErrorMessage";
export const SET_CURRENT_DATE = "Calendar->uiActions::setCurrentDate";
export const SET_VIEW = "Calendar->uiActions::setView";
export const SET_DELETING = "Calendar->uiActions::setDeleting";
export const SET_DELETE_ERROR_MESSAGE =
  "Calendar->uiActions::setDeleteErrorMessage";
export const SET_FETCH_ERROR_MESSAGE =
  "Calendar->uiActions::setFetchErrorMessage";
export const SET_FETCH_PARAMS = "Calendar->uiActions::setFetchParams";

export const toServerDate = (date, allDay = false) =>
  allDay
    ? format(date, "yyyy-MM-dd HH:mm:ss")
    : formatInTimeZone(date, serverTimezone, "yyyy-MM-dd HH:mm:ss");

export const adjustForUTCOffset = date => {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );
};

const timezone = moment.tz.guess();
export const openModal = (mode = "create", values = {}) => {
  return {
    type: OPEN_MODAL,
    payload: {
      mode,
      values
    }
  };
};

export const closeModal = () => {
  return {
    type: CLOSE_MODAL
  };
};

export const setSending = payload => {
  return {
    type: SET_SENDING,
    payload
  };
};

export const setModalErrorMessage = payload => {
  return {
    type: SET_MODAL_ERROR_MESSAGE,
    payload
  };
};

export const setFetchErrorMessage = payload => {
  return {
    type: SET_FETCH_ERROR_MESSAGE,
    payload
  };
};

export const setFetchParams = payload => {
  return {
    type: SET_FETCH_PARAMS,
    payload
  };
};

export const setDeleteErrorMessage = payload => {
  return {
    type: SET_DELETE_ERROR_MESSAGE,
    payload
  };
};

export const setView = payload => {
  return {
    type: SET_VIEW,
    payload
  };
};

export const setDeleting = payload => {
  return {
    type: SET_DELETING,
    payload
  };
};

export const setCurrentDate = (currentDate, view) => {
  return {
    type: SET_CURRENT_DATE,
    payload: {
      currentDate,
      view
    }
  };
};

export const remindEvent = payload => {
  return {
    type: REMIND_EVENT,
    payload
  };
};

export const cleanReminders = () => {
  return {
    type: CLEAN_REMINDERS
  };
};

export const refreshEvents = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const params = state.calendar?.ui?.fetchParams;
    params?.start &&
      params?.end &&
      dispatch(
        fetchEvents({
          start: params.start,
          end: params.end,
          tag: params.tag,
          calendarId: params.calendarId,
          refresh: true
        })
      );
  };
};

export const fetchEvents = ({
  start,
  end,
  tag,
  calendarId,
  refresh = false
}) => {
  const params = { start, end, tag, calendarId };
  start = Date.parse(start) / 1000;
  end = Date.parse(end) / 1000;

  return async (dispatch, getState) => {
    const state = getState();
    const loaded = state.calendar.entities.loaded;

    const inRange = loaded.some(
      event => event.start >= start && event.end <= end
    );

    if (inRange && !refresh) {
      return false;
    }

    dispatch(setFetchErrorMessage(null));
    dispatch(mainUiActions.setLoading(true));
    try {
      const result = await axiosV2.get("calendars", {
        params: {
          startdate: start,
          enddate: end,
          starttimezone: timezone,
          endtimezone: timezone,
          tag: tag,
          ...(calendarId ? { calendar_id: calendarId } : {})
        }
      });
      dispatch(entityActions.reset());
      dispatch(
        entityActions.fetchEventsSuccess({ start, end, events: result.data })
      );
      dispatch(setFetchParams(params));
    } catch (err) {
      dispatch(setFetchErrorMessage(err?.errorMessage));
    } finally {
      dispatch(mainUiActions.setLoading(false));
    }
  };
};

export const saveEvent = ({
  name,
  start,
  end,
  attendees,
  description,
  all_day,
  tags,
  location,
  alarm,
  alarm_timing_flag,
  recurrence,
  calendar_id
}) => {
  return async (dispatch, getState) => {
    dispatch(setModalErrorMessage(null));
    dispatch(setSending(true));
    const startdate = toServerDate(start, all_day);
    const enddate = toServerDate(end, all_day);

    if (recurrence?.enddate && typeof recurrence.enddate !== "string") {
      recurrence.enddate = format(recurrence.enddate, "yyyy-MM-dd");
    }

    try {
      // The calendar picked in the modal, else the one open in the sidebar
      const calendarId =
        calendar_id || getState()?.calendar?.ui?.fetchParams?.calendarId;
      const result = await axiosV2.post("calendars", {
        ...(calendarId ? { calendar_id: Number(calendarId) } : {}),
        name,
        text_content: description,
        html_content: description,
        status: "TENT",
        class: "PRI",
        free_busy_status: "busy",
        all_day: all_day,
        attendees,
        tags,
        location,
        alarm: parseInt(alarm),
        alarm_timing_flag,
        recurrence,
        instances: [
          {
            startdate,
            enddate,
            starttimezone: serverTimezone,
            endtimezone: serverTimezone
          }
        ]
      });

      dispatch(entityActions.createEventIfNotExists(result.data));
      dispatch(closeModal());
      dispatch(refreshEvents());
    } catch (err) {
      dispatch(setModalErrorMessage(err?.errorMessage));
    } finally {
      dispatch(setSending(false));
    }
  };
};

export const deleteEvent = (id, single_start_date, toggle) => {
  return async dispatch => {
    dispatch(setDeleteErrorMessage(null));
    dispatch(setDeleting(true));
    try {
      await axiosV2.delete("calendars/" + idDecode(id), {
        data: { single_start_date }
      });
      dispatch(entityActions.deleteEvent(id));
      toggle();
    } catch (err) {
      dispatch(setDeleteErrorMessage(err?.errorMessage));
    } finally {
      dispatch(setDeleting(false));
    }
  };
};

export const updateEvent = (
  id,
  {
    name,
    start,
    end,
    attendees,
    description,
    all_day,
    location,
    alarm,
    alarm_timing_flag,
    recurrence,
    single_start_date
  }
) => {
  const startdate = toServerDate(start, all_day);
  const enddate = toServerDate(end, all_day);

  if (recurrence?.enddate && typeof recurrence.enddate !== "string") {
    recurrence.enddate = format(recurrence.enddate, "yyyy-MM-dd");
  }

  return async dispatch => {
    dispatch(setModalErrorMessage(null));
    dispatch(setSending(true));
    let data = {
      name,
      attendees,
      text_content: description || null,
      html_content: description || null,
      all_day: all_day,
      status: "TENT",
      class: "PRI",
      free_busy_status: "busy",
      location: location,
      alarm: parseInt(alarm),
      alarm_timing_flag,
      recurrence,
      instances: [
        {
          startdate,
          enddate,
          starttimezone: serverTimezone,
          endtimezone: serverTimezone
        }
      ]
    };

    if (single_start_date) {
      data.single_start_date = single_start_date;
    }

    try {
      const result = await axiosV2.put(`calendars/${idDecode(id)}`, data);
      dispatch(entityActions.updateEvent(id, result.data));
      dispatch(closeModal());
      dispatch(refreshEvents());
    } catch (err) {
      dispatch(setModalErrorMessage(err?.errorMessage));
    } finally {
      dispatch(setSending(false));
    }
  };
};
